import Swal from "sweetalert2";

const DEFAULT_PER_PAGE = 25;

const emptyPage = () => ({
  data: [],
  meta: { current_page: 1, last_page: 1, total: 0, per_page: DEFAULT_PER_PAGE },
});

/**
 * Show why opening or downloading a document failed. `err` (the interceptor's rejection) is
 * `undefined` on a network failure, so it's guarded before use. Skips `accessDenied`, which the
 * interceptor already alerted for.
 */
const reportDocumentFailure = (err, fallback) => {
  if (err && err.accessDenied) {
    return;
  }

  const body = (err && err.data) || {};
  Swal.fire(
    "Error",
    (body.error || {}).message || body.message || fallback,
    "error"
  );
};

/**
 * Deleting the last row on the last page leaves `page` beyond `last_page`, and Laravel answers
 * with an empty list rather than clamping — while the pager hides itself, stranding the user.
 */
const clampedPage = (context, key, meta) => {
  const page = context.state[key].page;
  if (meta && meta.last_page && page > meta.last_page) {
    context.commit("PATCH_QUERY", { key, patch: { page: meta.last_page } });

    return true;
  }

  return false;
};

let getRequestId = 0;
let getSharedWithMeRequestId = 0;
let getContentsRequestId = 0;

export default {
  namespaced: true,
  state: {
    // The folder list, and the query that produced it.
    folders: emptyPage(),
    query: {
      search: "",
      sort: "name",
      direction: "asc",
      page: 1,
      perPage: DEFAULT_PER_PAGE,
      hideEmpty: false,
    },

    // The "Shared with me" tab — loose documents, with its own paging.
    sharedWithMe: emptyPage(),
    sharedQuery: { search: "", sort: "added", direction: "desc", page: 1 },

    // The open folder's contents.
    contents: emptyPage(),

    contentsQuery: {
      search: "",
      sort: "last_activity",
      direction: "desc",
      page: 1,
      hideEmpty: false,
    },

    isLoading: false,
    // Its own flag, not shared with `isLoading`: both lists fetch together on open, and one
    // flag would clear while the other request is still in flight.
    isLoadingShared: false,
    isLoadingContents: false,
    isSaving: false,
    isSharing: false,

    // False until the folder list has answered for the current subject — see RESET_LISTS.
    hasLoaded: false,
  },
  mutations: {
    SET_STATE: (state, obj) => {
      state[`${obj.key}`] = obj.value;
    },
    PATCH_QUERY: (state, { key, patch }) => {
      state[key] = { ...state[key], ...patch };
    },
    /**
     * Drop the previous subject's rows and counts — the module is a singleton, so without this
     * the header would show the last subject's totals until the new response lands.
     */
    RESET_LISTS: (state) => {
      state.folders = emptyPage();
      state.sharedWithMe = emptyPage();
      state.contents = emptyPage();
      state.hasLoaded = false;
    },
    RESET_QUERIES: (state) => {
      state.query = {
        search: "",
        sort: "name",
        direction: "asc",
        page: 1,
        perPage: DEFAULT_PER_PAGE,
        hideEmpty: false,
      };
      state.sharedQuery = {
        search: "",
        sort: "added",
        direction: "desc",
        page: 1,
      };
      state.contentsQuery = {
        search: "",
        sort: "last_activity",
        direction: "desc",
        page: 1,
        hideEmpty: false,
      };
    },
  },
  actions: {
    // ---- folder list --------------------------------------------------------

    get(context, userId) {
      const q = context.state.query;
      const params = {
        user_id: userId || undefined,
        search: q.search || undefined,
        sort: q.sort,
        direction: q.direction,
        page: q.page,
        per_page: q.perPage,
        hide_empty: q.hideEmpty ? 1 : undefined,
      };

      // A slower older response must not overwrite a newer one, or clear its loading flag.
      const requestId = ++getRequestId;
      context.commit("SET_STATE", { key: "isLoading", value: true });

      return this.$http
        .get("documents/folders", { params })
        .then((res) => {
          if (requestId !== getRequestId) {
            return;
          }
          if (clampedPage(context, "query", res.data.meta)) {
            return context.dispatch("get", userId);
          }
          context.commit("SET_STATE", {
            key: "folders",
            value: { data: res.data.data, meta: res.data.meta },
          });
          context.commit("SET_STATE", { key: "hasLoaded", value: true });
        })
        .catch((err) => {
          if (requestId !== getRequestId) {
            return;
          }
          if (!err || !err.accessDenied) {
            Swal.fire("Error", "Could not load folders.", "error");
          }
        })
        .finally(() => {
          if (requestId !== getRequestId) {
            return;
          }
          context.commit("SET_STATE", { key: "isLoading", value: false });
        });
    },

    // ---- shared with me -----------------------------------------------------

    getSharedWithMe(context, userId) {
      const q = context.state.sharedQuery;
      const params = {
        user_id: userId || undefined,
        search: q.search || undefined,
        sort: q.sort,
        direction: q.direction,
        page: q.page,
      };

      // Same stale-response race as `get()` above, on this list's own counter.
      const requestId = ++getSharedWithMeRequestId;
      context.commit("SET_STATE", { key: "isLoadingShared", value: true });

      return this.$http
        .get("documents/shared-with-me", { params })
        .then((res) => {
          if (requestId !== getSharedWithMeRequestId) {
            return;
          }
          if (clampedPage(context, "sharedQuery", res.data.meta)) {
            return context.dispatch("getSharedWithMe", userId);
          }
          context.commit("SET_STATE", {
            key: "sharedWithMe",
            value: { data: res.data.data, meta: res.data.meta },
          });
        })
        .catch((err) => {
          if (requestId !== getSharedWithMeRequestId) {
            return;
          }
          if (!err || !err.accessDenied) {
            Swal.fire("Error", "Could not load shared documents.", "error");
          }
        })
        .finally(() => {
          if (requestId !== getSharedWithMeRequestId) {
            return;
          }
          context.commit("SET_STATE", { key: "isLoadingShared", value: false });
        });
    },

    // ---- one folder's contents ---------------------------------------------

    getContents(context, folderId) {
      const q = context.state.contentsQuery;
      const params = {
        folder_id: folderId,
        search: q.search || undefined,
        hide_empty: q.hideEmpty ? 1 : undefined,
        sort: q.sort,
        direction: q.direction,
        page: q.page,
      };

      // Subfolders first, then documents. Same stale-response race as `get()` above.
      const requestId = ++getContentsRequestId;
      context.commit("SET_STATE", { key: "isLoadingContents", value: true });

      return this.$http
        .get("documents/folders", { params })
        .then((res) => {
          if (requestId !== getContentsRequestId) {
            return;
          }
          if (clampedPage(context, "contentsQuery", res.data.meta)) {
            return context.dispatch("getContents", folderId);
          }
          context.commit("SET_STATE", {
            key: "contents",
            value: { data: res.data.data, meta: res.data.meta },
          });
        })
        .catch((err) => {
          if (requestId !== getContentsRequestId) {
            return;
          }
          if (!err || !err.accessDenied) {
            Swal.fire("Error", "Could not load this folder.", "error");
          }
          // Re-thrown so the screen can step back out: it had already moved the breadcrumb
          // into a folder that never opened.
          throw err;
        })
        .finally(() => {
          if (requestId !== getContentsRequestId) {
            return;
          }
          context.commit("SET_STATE", {
            key: "isLoadingContents",
            value: false,
          });
        });
    },

    // ---- audience & share preview ------------------------------------------

    getAudience(_, folderId) {
      return this.$http
        .get(`documents/folders/${folderId}/audience`)
        .then((res) => res.data.data);
    },

    /**
     * "N people will gain access" — never recompute this from the recipient list, the backend
     * runs the SAME code the share itself does.
     *
     * `directShare` IS sent: a non-direct share to a client files it against her record without
     * giving her real access (see DocumentAccess), so the preview needs it to tell her apart
     * from a recipient who will actually be able to open it.
     */
    previewShare(_, { folderId, userIds, directShare }) {
      return this.$http
        .post("documents/folders/share/preview", {
          folders_id: `[${folderId}]`,
          users_id: `[${userIds}]`,
          directShare,
        })
        .then((res) => res.data.data);
    },

    // ---- pin ----------------------------------------------------------------

    pin(_, folderId) {
      return this.$http.post(`documents/folders/${folderId}/pin`);
    },

    unpin(_, folderId) {
      return this.$http.delete(`documents/folders/${folderId}/pin`);
    },

    // ---- create / rename / delete --------------------------------------------

    save(context, payload) {
      context.commit("SET_STATE", { key: "isSaving", value: true });

      return this.$http
        .post("documents/folders", payload)
        .then((res) => res.data)
        .finally(() => {
          context.commit("SET_STATE", { key: "isSaving", value: false });
        });
    },

    rename(_, { folderId, name }) {
      return this.$http
        .put(`documents/folders/${folderId}`, { name })
        .then(() => true)
        .catch((err) => {
          reportDocumentFailure(err, "Could not rename this folder.");
          return false;
        });
    },

    move(_, { folderId, targetFolderId }) {
      return this.$http
        .put(`documents/folders/${folderId}/move`, {
          folder_id: targetFolderId,
        })
        .then(() => true)
        .catch((err) => {
          reportDocumentFailure(err, "Could not move this folder.");
          return false;
        });
    },

    deleteFolder(_, payload) {
      const query = payload.userId ? `?user_id=${payload.userId}` : "";

      return this.$http
        .delete(`documents/folders/${payload.folderId}${query}`)
        .then(() => true)
        .catch((err) => {
          reportDocumentFailure(err, "Could not delete this folder.");
          return false;
        });
    },

    deleteDocument(_, id) {
      return this.$http
        .delete(`documents/${id}`)
        .then(() => true)
        .catch((err) => {
          reportDocumentFailure(err, "Could not delete this document.");
          return false;
        });
    },

    // ---- view / download ----------------------------------------------------

    /**
     * Both report failures via `reportDocumentFailure` and re-throw, so callers still land in
     * their `.finally()` and stop their spinners. Neither had a handler before — an unhandled
     * 403 or 404 left the spinner cleared with no message at all.
     */
    download(_, doc) {
      return this.$http
        .get(`documents/${doc.id}?owner_id=${doc.owner_id}&download=${true}`)
        .then((res) => {
          const link = document.createElement("a");
          link.href = res.data.url;
          link.setAttribute("download", res.data.filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return res.data.url;
        })
        .catch((err) => {
          reportDocumentFailure(err, "Could not download this document.");
          throw err;
        });
    },

    view(_, doc) {
      return this.$http
        .get(`documents/${doc.id}?owner_id=${doc.owner_id}`)
        .then((res) => res.data.url)
        .catch((err) => {
          reportDocumentFailure(err, "Could not open this document.");
          throw err;
        });
    },

    // ---- sharing ------------------------------------------------------------

    shareFiles(context, payload) {
      context.commit("SET_STATE", { key: "isSharing", value: true });

      return this.$http
        .post("documents/share", payload)
        .then(() => {
          Swal.fire({
            title: "",
            text: "Document(s) shared successfully",
            icon: "success",
          });
          return true;
        })
        .catch(({ data, accessDenied } = {}) => {
          // A duplicate row means it was already shared, so intent is satisfied — not an error.
          const message = ((data || {}).error || {}).message || "";
          if (message.indexOf("Duplicate entry") > -1) {
            Swal.fire({
              title: "",
              text: "Document(s) shared successfully",
              icon: "success",
            });
            return true;
          }
          if (!accessDenied) {
            Swal.fire({
              title: "Error",
              text: "Something went wrong...",
              icon: "error",
            });
          }
          return false;
        })
        .finally(() => {
          context.commit("SET_STATE", { key: "isSharing", value: false });
        });
    },

    shareFolders(context, payload) {
      context.commit("SET_STATE", { key: "isSharing", value: true });

      return this.$http
        .post("documents/folders/share", payload)
        .then(() => {
          Swal.fire({
            title: "",
            text: "Folder shared successfully",
            icon: "success",
          });
          return true;
        })
        .catch((err) => {
          if (!err || !err.accessDenied) {
            Swal.fire({
              title: "Error",
              text: "Something went wrong...",
              icon: "error",
            });
          }
          return false;
        })
        .finally(() => {
          context.commit("SET_STATE", { key: "isSharing", value: false });
        });
    },
  },
};
